import { useEffect, useLayoutEffect, useRef } from "react";

export type KeyHandlers = Record<string, (e: KeyboardEvent) => void>;

/**
 * Presenter key map. Keys are KeyboardEvent.key values (lower-cased letters). Ignores typing in
 * fields and any chord with Cmd/Ctrl/Alt so browser shortcuts (Cmd+R, Ctrl+L) keep working.
 */
export function useKeyboard(handlers: KeyHandlers) {
  const ref = useRef(handlers);
  useLayoutEffect(() => {
    ref.current = handlers;
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName))) return;
      // Enter on a focused control activates that control, not the presenter shortcut.
      if (e.key === "Enter" && t && ["BUTTON", "A"].includes(t.tagName)) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const handler = ref.current[key];
      if (!handler) return;
      e.preventDefault();
      handler(e);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
